import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Key,
  Plus,
  Shield,
  ShieldOff,
  Loader2,
  AlertTriangle,
  Clock,
  RefreshCw,
  KeyRound,
} from "lucide-react";
import { format } from "date-fns";
import {
  useMasterKeys,
  useCreateMasterKey,
  useRevokeMasterKey,
  MasterKey,
  EncryptionAlgorithm,
  ALGORITHM_LABELS,
} from "@/hooks/useEncryptionPolicies";
import { hashKey } from "@/hooks/useEncryptionKeyManagement";

const STATUS_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  pending_rotation: "secondary",
  rotated: "outline",
  revoked: "destructive",
  compromised: "destructive",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  pending_rotation: "Pending Rotation",
  rotated: "Rotated",
  revoked: "Revoked",
  compromised: "Compromised",
};

export function MasterKeyManagement() {
  const { data: masterKeys, isLoading, refetch } = useMasterKeys();
  const createMutation = useCreateMasterKey();
  const revokeMutation = useRevokeMasterKey();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<MasterKey | null>(null);

  // Create form state
  const [keyAlias, setKeyAlias] = useState("");
  const [newKeyValue, setNewKeyValue] = useState("");
  const [confirmKeyValue, setConfirmKeyValue] = useState("");
  const [algorithm, setAlgorithm] = useState<EncryptionAlgorithm>("AES-256-GCM");
  const [keyLengthBits, setKeyLengthBits] = useState(256);
  const [expiresAt, setExpiresAt] = useState("");
  const [revokeReason, setRevokeReason] = useState("");

  const activeKeys = masterKeys?.filter((k) => k.status === "active") ?? [];

  const resetCreateForm = () => {
    setKeyAlias("");
    setNewKeyValue("");
    setConfirmKeyValue("");
    setAlgorithm("AES-256-GCM");
    setKeyLengthBits(256);
    setExpiresAt("");
  };

  const handleCreate = async () => {
    if (!keyAlias.trim()) {
      toast.error("Key alias is required");
      return;
    }
    if (!newKeyValue || newKeyValue.length < 32) {
      toast.error("Key must be at least 32 characters");
      return;
    }
    if (newKeyValue !== confirmKeyValue) {
      toast.error("Keys do not match");
      return;
    }

    try {
      const keyHash = await hashKey(newKeyValue);
      await createMutation.mutateAsync({
        key_alias: keyAlias.trim(),
        key_hash: keyHash,
        algorithm,
        key_length_bits: keyLengthBits,
        expires_at: expiresAt || null,
      });
      toast.success("Master key created successfully");
      setCreateDialogOpen(false);
      resetCreateForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create master key");
    }
  };

  const handleRevoke = async () => {
    if (!selectedKey) return;

    try {
      await revokeMutation.mutateAsync({
        key_id: selectedKey.id,
        reason: revokeReason || undefined,
      });
      toast.success("Master key revoked successfully");
      setRevokeDialogOpen(false);
      setSelectedKey(null);
      setRevokeReason("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to revoke master key");
    }
  };

  const openRevokeDialog = (key: MasterKey) => {
    setSelectedKey(key);
    setRevokeReason("");
    setRevokeDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium">Platform Master Keys</h3>
          <p className="text-sm text-muted-foreground">
            Manage master encryption keys for the platform
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Master Key
          </Button>
        </div>
      </div>

      {/* Active Keys Summary */}
      {activeKeys.length === 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Active Master Keys</AlertTitle>
          <AlertDescription>
            There are no active master keys. Create a master key to enable platform encryption.
          </AlertDescription>
        </Alert>
      )}

      {/* Keys Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Master Keys
          </CardTitle>
          <CardDescription>
            All platform master keys and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {masterKeys && masterKeys.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alias</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Algorithm</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Recovery</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {masterKeys.map((key) => (
                    <TableRow key={key.id}>
                      <TableCell className="font-medium">{key.key_alias}</TableCell>
                      <TableCell>v{key.key_version}</TableCell>
                      <TableCell>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {key.algorithm}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_COLORS[key.status]}>
                          {STATUS_LABELS[key.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {format(new Date(key.created_at), "MMM d, yyyy")}
                        </span>
                      </TableCell>
                      <TableCell>
                        {key.expires_at ? (
                          <span className="text-sm">
                            {format(new Date(key.expires_at), "MMM d, yyyy")}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {key.has_recovery_codes ? (
                          <Badge variant="secondary">
                            {key.recovery_codes_count} codes
                          </Badge>
                        ) : (
                          <span className="text-sm text-muted-foreground">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {key.status === "active" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openRevokeDialog(key)}
                          >
                            <ShieldOff className="h-4 w-4 mr-1" />
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No master keys found</p>
              <p className="text-sm">Create your first master key to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Master Key</DialogTitle>
            <DialogDescription>
              Create a new platform master key for encryption operations
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive" className="my-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Critical:</strong> Store this key securely. Lost master keys cannot be recovered
              without emergency recovery codes.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="keyAlias">Key Alias</Label>
              <Input
                id="keyAlias"
                placeholder="e.g., production-master, backup-key"
                value={keyAlias}
                onChange={(e) => setKeyAlias(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                A unique identifier for this master key
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Algorithm</Label>
                <Select value={algorithm} onValueChange={(v) => setAlgorithm(v as EncryptionAlgorithm)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ALGORITHM_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Key Length</Label>
                <Select
                  value={keyLengthBits.toString()}
                  onValueChange={(v) => setKeyLengthBits(parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="128">128 bits</SelectItem>
                    <SelectItem value="256">256 bits</SelectItem>
                    <SelectItem value="384">384 bits</SelectItem>
                    <SelectItem value="512">512 bits</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newKey">Master Key Value</Label>
              <Input
                id="newKey"
                type="password"
                placeholder="Enter master key (min 32 characters)"
                value={newKeyValue}
                onChange={(e) => setNewKeyValue(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmKey">Confirm Key Value</Label>
              <Input
                id="confirmKey"
                type="password"
                placeholder="Re-enter master key"
                value={confirmKeyValue}
                onChange={(e) => setConfirmKeyValue(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiresAt">Expiration Date (Optional)</Label>
              <Input
                id="expiresAt"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                resetCreateForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Shield className="h-4 w-4 mr-2" />
              Create Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke Master Key</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke the master key "{selectedKey?.key_alias}"?
            </DialogDescription>
          </DialogHeader>

          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> Revoking a master key is irreversible. Any data encrypted with
              this key may become inaccessible.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="revokeReason">Reason for Revocation</Label>
            <Textarea
              id="revokeReason"
              placeholder="e.g., Key compromised, Scheduled rotation, Personnel change..."
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRevokeDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revokeMutation.isPending}
            >
              {revokeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <ShieldOff className="h-4 w-4 mr-2" />
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
